import { useI18n } from "../i18n/I18nContext";
import { LANGUAGES, type Lang } from "../i18n/translations";

interface Props {
  /** Visual variant. "header" is white-on-dark; "light" is dark text on light bg. */
  variant?: "header" | "light";
  className?: string;
}

/**
 * Compact language switcher.
 *
 * NOTE: this only changes UI text. The Excel export from the admin panel
 * is generated server-side and intentionally stays in Swedish.
 */
export function LanguageSwitcher({ variant = "header", className = "" }: Props) {
  const { lang, setLang, t } = useI18n();

  const baseBtn =
    "px-2 py-1 text-xs font-semibold uppercase tracking-wide transition";
  const isHeader = variant === "header";
  const groupCls = isHeader
    ? "inline-flex items-center rounded-lg border border-white/20 bg-white/5 p-0.5"
    : "inline-flex items-center rounded-lg border border-border bg-white p-0.5";

  return (
    <div
      className={`${groupCls} ${className}`.trim()}
      role="group"
      aria-label={t("lang.label")}
    >
      {LANGUAGES.map((l) => {
        const active = l.code === lang;
        const cls = isHeader
          ? active
            ? "bg-white text-portal-primary rounded-md"
            : "text-white/85 hover:bg-white/10 rounded-md"
          : active
            ? "bg-portal-primary text-white rounded-md"
            : "text-text-secondary hover:bg-surface-alt rounded-md";
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code as Lang)}
            className={`${baseBtn} ${cls}`}
            aria-pressed={active}
            title={l.label}
          >
            {l.code === "sv" ? "SV" : "EN"}
          </button>
        );
      })}
    </div>
  );
}
